/* ==========================================================================
   <hero-media> — background video controller
   - silent autoplay on loop with an image poster/fallback
   - optional play/pause control per video
   - respects prefers-reduced-motion
   - pauses while off-screen to save resources
   - supports multiple videos in one section (e.g. V4 panel + split)
   ========================================================================== */
(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class VideoControl {
    constructor(video, toggle) {
      this.video = video;
      this.toggle = toggle;
      // Paused state we want to preserve across intersection changes.
      this.userPaused = REDUCED_MOTION;

      video.addEventListener('play', () => this._setState(true));
      video.addEventListener('pause', () => this._setState(false));

      if (toggle) {
        toggle.addEventListener('click', () => {
          if (video.paused) {
            this.userPaused = false;
            this._play();
          } else {
            this.userPaused = true;
            video.pause();
          }
        });
      }

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.userPaused) this._play();
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.15 }
        );
        this._observer.observe(video);
      } else if (!this.userPaused) {
        this._play();
      }

      this._setState(!video.paused);
    }

    _play() {
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          /* autoplay can be blocked — leave the poster visible */
        });
      }
    }

    _setState(playing) {
      if (!this.toggle) return;
      this.toggle.dataset.playing = playing ? 'true' : 'false';
      this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    destroy() {
      if (this._observer) this._observer.disconnect();
    }
  }

  class HeroMedia extends HTMLElement {
    connectedCallback() {
      this.controls = [];
      this.querySelectorAll('.hero__video').forEach((video) => {
        const container = video.closest('.hero__media, .hero__panel-media') || this;
        const toggle = container.querySelector('[data-hero-toggle]');
        this.controls.push(new VideoControl(video, toggle));
      });
    }

    disconnectedCallback() {
      this.controls.forEach((control) => control.destroy());
    }
  }

  if (!customElements.get('hero-media')) {
    customElements.define('hero-media', HeroMedia);
  }
})();
